import React, { useMemo } from "react";
import { usePiCycleTopChart } from "@/hooks/coinglass/usePiCycleTopChart";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { EChartsOption } from "echarts";

interface PiCycleTopChartProps {
  startDate?: number;
  endDate?: number;
  limit?: number;
  height?: string | number;
}

export const PiCycleTopChart: React.FC<PiCycleTopChartProps> = ({
  startDate,
  endDate,
  limit,
  height = 500,
}) => {
  const { data, isLoading, isError, error } = usePiCycleTopChart({
    start_timestamp: startDate,
    end_timestamp: endDate,
    limit,
  });

  const option = useMemo<EChartsOption | null>(() => {
    if (!data?.data) return null;

    const { xAxis, series } = data.data;

    // Detect crossovers for markPoints
    const crossoverDates: string[] = [];
    if (series.length >= 3) {
      const ma111 = series[1]?.data;
      const ma350x2 = series[2]?.data;
      
      if (ma111 && ma350x2 && ma111.length === ma350x2.length) {
        for (let i = 1; i < ma111.length; i++) {
          const prevBelow = (ma111[i - 1] || 0) < (ma350x2[i - 1] || 0);
          const currAbove = (ma111[i] || 0) > (ma350x2[i] || 0);

          if (prevBelow && currAbove) {
            const date = xAxis[i];
            if (date) crossoverDates.push(date);
          }
        }
      }
    }

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: "rgba(20, 20, 30, 0.9)",
        borderColor: "#333",
        textStyle: { color: "#fff" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          
          const p = params;
          const date = p[0].axisValue;
          
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const rows = p.map((item: any) => `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="color: #d1d5db; margin-right: 12px;">${item.seriesName}:</span>
              <span style="font-weight: bold; color: ${item.color};">$${typeof item.data === 'number' ? item.data.toLocaleString() : item.data}</span>
            </div>
          `).join('');

          return `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: bold; margin-bottom: 8px; color: #9ca3af;">${date}</div>
              ${rows}
            </div>
          `;
        }
      },
      legend: {
        data: ["BTC Price", "111-Day MA", "350-Day MA x2"],
        textStyle: { color: "#9ca3af" },
        top: 0,
        left: "center",
        icon: "circle",
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "12%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxis,
        axisLabel: {
          color: "#9ca3af",
          hideOverlap: true,
          formatter: (value: string) => {
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          },
        },
        axisLine: { lineStyle: { color: "#374151" } },
      },
      yAxis: {
        type: "value",
        name: "Price (USD)",
        position: "right",
        splitLine: { lineStyle: { color: "#1f2937" } },
        axisLabel: {
          color: "#9ca3af",
          fontWeight: "normal",
          formatter: (value: number) => {
            if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
            return `$${value}`;
          },
        },
        axisLine: { lineStyle: { color: "#374151" } },
      },
      series: series.map((s, index) => {
        let color = "#fff";
        if (index === 0) color = "#fbbf24"; // BTC Price
        else if (index === 1) color = "#ef4444"; // 111-Day MA
        else if (index === 2) color = "#10b981"; // 350-Day MA x2

        return {
          ...s,
          itemStyle: { color },
          lineStyle: { width: 2 },
          symbol: "none",
          markPoint: index === 0 ? {
            data: crossoverDates.map(date => ({
              xAxis: date,
              yAxis: series[0]?.data?.[xAxis.indexOf(date)],
              symbol: "pin",
              symbolSize: 50,
              label: { 
                formatter: "Top",
                color: "#fff",
                fontSize: 10
              },
              itemStyle: { color: "#ef4444" }
            }))
          } : undefined
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
      dataZoom: [
        { type: "inside", start: 70, end: 100 },
        {
          type: "slider",
          show: true,
          bottom: 30,
          height: 20,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af" },
          brushSelect: false,
        },
      ],
    };
  }, [data]);

  return (
    <EChartsRenderer
      chartOption={option || {}}
      height={height}
      isLoading={isLoading}
      error={isError ? (error as Error).message : null}
      hasData={!!data?.data}
      containerClassName="w-full h-full"
    />
  );
};
